/**
 * ChordDiagram — SVG guitar fingering diagram popup.
 *
 * Shows when user hovers over a chord name in the chord sheet.
 * Uses @tombatossals/chords-db for chord voicing data.
 */

import React, { useMemo } from 'react';
import guitarChords from '@tombatossals/chords-db/lib/guitar.json';
import './ChordDiagram.css';

const STRINGS = 6;
const FRETS_SHOWN = 5;
const CELL_W = 24;
const CELL_H = 20;
const MARGIN_L = 28;
const MARGIN_T = 22;

const SVG_W = MARGIN_L + CELL_W * (STRINGS - 1) + 16;
const SVG_H = MARGIN_T + CELL_H * FRETS_SHOWN + 14;

/* Map chord root names to chords-db keys */
const ROOT_MAP = {
  'C': 'C',   'C#': 'Csharp', 'Db': 'Csharp',
  'D': 'D',   'D#': 'Eb',     'Eb': 'Eb',
  'E': 'E',   'F': 'F',       'F#': 'Fsharp', 'Gb': 'Fsharp',
  'G': 'G',   'G#': 'Ab',     'Ab': 'Ab',
  'A': 'A',   'A#': 'Bb',     'Bb': 'Bb',
  'B': 'B',
};

/* Map chord quality suffixes to chords-db suffix names */
const SUFFIX_MAP = {
  '':      'major',
  'maj':   'major',
  'm':     'minor',
  'min':   'minor',
  '7':     '7',
  'maj7':  'maj7',
  'm7':    'm7',
  'dim':   'dim',
  'dim7':  'dim7',
  'aug':   'aug',
  'sus2':  'sus2',
  'sus4':  'sus4',
  '6':     '6',
  'm6':    'm6',
  '9':     '9',
  'maj9':  'maj9',
  'm9':    'm9',
  'add9':  'add9',
  '11':    '11',
  '13':    '13',
};

function parseChordName(chord) {
  if (!chord) return null;
  const normalized = String(chord)
    .replace(/♯/g, '#')
    .replace(/♭/g, 'b');
  // Strip slash bass
  const base = normalized.replace(/\/.*$/, '');
  const match = base.match(/^([A-G][b#]?)(.*)/);
  if (!match) return null;
  const [, root, suffix] = match;
  return { root, suffix: suffix.toLowerCase().trim() };
}

function lookupChordPositions(chord) {
  const parsed = parseChordName(chord);
  if (!parsed) return null;

  const dbKey = ROOT_MAP[parsed.root];
  if (!dbKey) return null;

  const entries = guitarChords.chords[dbKey];
  if (!entries) return null;

  const suffixKey = SUFFIX_MAP[parsed.suffix] ?? SUFFIX_MAP[parsed.suffix.replace('m', 'min')] ?? 'major';
  const entry = entries.find(e => e.suffix === suffixKey);
  if (!entry) return null;
  return entry.positions;
}

function DiagramSVG({ position, baseFret }) {
  const { frets, barres = [] } = position;

  const fretOffset = (baseFret ?? 1) - 1;
  const toFiniteNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const gridLines = [];
  // Horizontal fret lines
  for (let f = 0; f <= FRETS_SHOWN; f++) {
    const y = MARGIN_T + f * CELL_H;
    gridLines.push(<line key={`h${f}`} x1={MARGIN_L} x2={MARGIN_L + CELL_W * (STRINGS - 1)} y1={y} y2={y} stroke="#888" strokeWidth={f === 0 ? 3 : 1} />);
  }
  // Vertical string lines
  for (let s = 0; s < STRINGS; s++) {
    const x = MARGIN_L + s * CELL_W;
    gridLines.push(<line key={`v${s}`} x1={x} x2={x} y1={MARGIN_T} y2={MARGIN_T + CELL_H * FRETS_SHOWN} stroke="#888" strokeWidth={1} />);
  }

  // Finger dots
  const dots = frets.map((fret, si) => {
    const x = MARGIN_L + si * CELL_W;
    if (fret === -1) {
      // Muted string
      return <text key={`m${si}`} x={x} y={MARGIN_T - 7} textAnchor="middle" fontSize="11" fill="#888">✕</text>;
    }
    if (fret === 0) {
      // Open string
      return <circle key={`o${si}`} cx={x} cy={MARGIN_T - 7} r={4} stroke="#888" strokeWidth={1.5} fill="none" />;
    }
    const actualFret = fret - fretOffset;
    if (actualFret < 1 || actualFret > FRETS_SHOWN) return null;
    const cy = MARGIN_T + (actualFret - 0.5) * CELL_H;
    return <circle key={`d${si}`} cx={x} cy={cy} r={8} fill="#333" />;
  });

  // Barre arcs
  const barreElems = barres.map((barre, bi) => {
    const fretNum = toFiniteNumber(barre?.fret);
    const fromString = toFiniteNumber(barre?.fromString);
    const toString = toFiniteNumber(barre?.toString);
    if (fretNum == null || fromString == null || toString == null) return null;

    const actualFret = fretNum - fretOffset;
    if (actualFret < 1 || actualFret > FRETS_SHOWN) return null;
    const y = MARGIN_T + (actualFret - 0.5) * CELL_H;
    const x1 = MARGIN_L + Math.min(fromString, toString) * CELL_W;
    const x2 = MARGIN_L + Math.max(fromString, toString) * CELL_W;
    const width = x2 - x1 + 16;
    if (!Number.isFinite(x1) || !Number.isFinite(y) || !Number.isFinite(width) || width <= 0) return null;
    return <rect key={`b${bi}`} x={x1 - 8} y={y - 8} width={width} height={16} rx={8} fill="#333" opacity={0.85} />;
  });

  // Fret number label (when not starting at fret 1)
  const fretLabel = (baseFret ?? 1) > 1 && (
    <text x={MARGIN_L - 10} y={MARGIN_T + CELL_H * 0.65} textAnchor="end" fontSize="10" fill="#555">{baseFret}fr</text>
  );

  return (
    <svg width={SVG_W} height={SVG_H} className="chord-diagram__svg">
      {gridLines}
      {barreElems}
      {dots}
      {fretLabel}
    </svg>
  );
}

/**
 * ChordDiagram popup component.
 *
 * @param {string}  chord       The displayed chord name (post-transpose)
 * @param {number}  capo        Capo position (for "play this shape" display)
 * @param {object}  anchorRect  DOMRect from the chord element (for positioning)
 * @param {boolean} visible
 * @param {number}  time        Optional timestamp in seconds for debug display
 */
function ChordDiagram({ chord, anchorRect, visible, time, beatInfo }) {
  const positions = useMemo(() => lookupChordPositions(chord), [chord]);

  if (!visible || !chord || !anchorRect) return null;

  const firstPosition = positions?.[0];

  // Estimated popup height to decide whether to flip above the anchor
  const POPUP_H = 195 + (time != null ? 28 : 0);
  const POPUP_W = SVG_W + 20;

  // Horizontal: center on chord token, clamped to viewport
  const rawLeft = anchorRect.left + anchorRect.width / 2 - POPUP_W / 2;
  const left = Math.min(Math.max(4, rawLeft), window.innerWidth - POPUP_W - 4);

  // Vertical: show below by default; flip above if too close to bottom
  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const top = spaceBelow >= POPUP_H + 10
    ? anchorRect.bottom + 6   // below — position: fixed, viewport coords (no scrollY)
    : anchorRect.top - POPUP_H - 6; // flip above

  const formatTime = (t) => {
    if (t == null) return null;
    const m = Math.floor(t / 60);
    const s = (t % 60).toFixed(2).padStart(5, '0');
    return `${m}:${s}`;
  };

  return (
    <div
      className="chord-diagram"
      style={{ left, top }}
      role="tooltip"
      aria-label={`Chord diagram for ${chord}`}
    >
      <div className="chord-diagram__title">{chord}</div>
      {firstPosition ? (
        <DiagramSVG position={firstPosition} baseFret={firstPosition.baseFret} />
      ) : (
        <div className="chord-diagram__no-data">No diagram available</div>
      )}
      {time != null && (
        <div className="chord-diagram__time">
          ⏱ {formatTime(time)}
          {beatInfo && (
            <span className="chord-diagram__beat"> · M:{beatInfo.measure}.{beatInfo.beat}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default ChordDiagram;
export { lookupChordPositions };
